import { matchers } from '../config/matchers';

type TagType = 'amount' | 'hashtag' | 'mention' | 'recipient';

export type TokenType = TagType | 'text';

const DEFAULT_ENABLED: TagType[] = ['amount', 'hashtag', 'mention', 'recipient'];

interface LexerOptions {
	enabled: TagType[];
}

export interface LexerToken {
	delimiter: string;
	type: TokenType;
	value: string;
}

const TaskList: Record<TokenType, any> = {
	text: {
		regexp: matchers.text.lex,
		delimiter: '',
	},
	amount: {
		regexp: matchers.amount.lex,
		delimiter: '+',
	},
	hashtag: {
		regexp: matchers.hashtag.lex,
		delimiter: '#',
	},
	recipient: {
		regexp: matchers.recipient.lex,
		delimiter: '@',
	},
	mention: {
		regexp: matchers.mention.lex,
		delimiter: '&',
	},
};

export class RichTextLexer {
	constructor(
		public readonly source: string = '',
		public readonly options: LexerOptions = { enabled: DEFAULT_ENABLED }
	) {}

	tokens() {
		const tokens: LexerToken[] = [];
		const tasks = Object.entries(TaskList);
		// Text matches until it hits a configured tag delimiter
		const textMatch = new RegExp(`^[^${this.textRegexp()}]+`);

		let subject = this.source;

		while (subject.length) {
			let match;

			for (const [type, task] of tasks) {
				if (!this.options.enabled.includes(type as TagType)) {
					match = subject.match(textMatch);

					if (match) {
						tokens.push({
							delimiter: '',
							type: 'text',
							value: match[0],
						});

						subject = subject.slice(match[0].length);

						break;
					}
				}

				match = subject.match(task.regexp);

				if (match) {
					// For mentions and recipients, check if the previous text ended with a word character.
					// If so, this is likely a URL query param (e.g., ?foo=bar&baz), not a real mention.
					// Treat the delimiter as plain text instead.
					if ((type === 'mention' || type === 'recipient') && tokens.length > 0) {
						const lastToken = tokens[tokens.length - 1];
						if (lastToken.type === 'text' && /\w$/.test(lastToken.value)) {
							// Append the delimiter to the previous text token and continue
							lastToken.value += task.delimiter;
							subject = subject.slice(1);
							match = null;
							break;
						}
					}

					// Amounts require whitespace or > before the + to match the server-side
					// BONUS_AMOUNT_REGEX: /(^|\s|>)\+(\d+)\b/
					// Without this, "you+100" would be parsed as an amount on the frontend
					// but rejected by the server, causing points to silently not apply.
					// We consume the full match (e.g., "+100") as plain text rather than
					// just the delimiter, so "you+100" becomes a single text token instead
					// of fragmented tokens that would introduce spurious spaces.
					if (type === 'amount' && tokens.length > 0) {
						const lastToken = tokens[tokens.length - 1];
						if (lastToken.type === 'text' && !/[\s>]$/.test(lastToken.value)) {
							lastToken.value += match[0];
							subject = subject.slice(match[0].length);
							match = null;
							break;
						}
					}

					tokens.push({
						delimiter: type === 'text' ? textMatch : task.delimiter,
						type: type as TokenType,
						value: task.delimiter ? match[0].slice(1) : match[0],
					});

					subject = subject.slice(match[0].length);

					break;
				}
			}

			// Fallback: if no matcher consumed input, treat the first character as text
			// This preserves stray delimiters like '&' that don't start a valid token
			// Enter the fallback path only when nothing matched at the current position
			if (!match) {
				// Grab the current character so it can be preserved as plain text
				const ch = subject[0];
				// Look at the previously emitted token to decide whether to merge
				const last = tokens[tokens.length - 1];

				// If the previous token is already text, append this character to it
				if (last && last.type === 'text') {
					last.value += ch;
				} else {
					// Otherwise, emit a new single-character text token
					tokens.push({ delimiter: '', type: 'text', value: ch });
				}

				// Advance the input by one character and continue lexing
				subject = subject.slice(1);
			}
		}

		return tokens;
	}

	private textRegexp() {
		return Object.entries(TaskList)
			.filter(([type]) => {
				return this.options.enabled.includes(type as TagType);
			})
			.map(([, task]) => task.delimiter)
			.join('');
	}
}
